"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronDown,
  HelpCircle,
  MessageCircleQuestion,
  ShieldCheck,
} from "lucide-react";

import Section from "./ui/Section";

const faqs = [
  {
    q: "What is THE CONCLUSION DAILY?",
    a: "THE CONCLUSION DAILY (TCD) is an online examination and competitive learning platform built for students, teachers, and educational institutes.",
  },
  {
    q: "Can institutes conduct their own exams?",
    a: "Yes. The institute platform supports creating and scheduling examinations, monitoring exam activity, and reviewing student performance.",
  },
  {
    q: "Does TCD support computer-based tests?",
    a: "Yes. Students can take supported online assessments through the platform. Follow the instructions shown for each examination, including any device or monitoring requirements.",
  },
  {
    q: "What does exam monitoring do?",
    a: "Depending on the examination settings and device support, monitoring tools may include camera checks, fullscreen checks, and recording important exam activity for review. These tools support exam supervision but cannot guarantee that every form of misconduct will be detected.",
  },
  {
    q: "Can students view rankings and performance?",
    a: "Students can review available results and rankings for supported exams or competitions. The information shown depends on the exam settings and when results are published.",
  },
  {
    q: "How do TCD Credits convert to rupees?",
    a: "The current display conversion is 100 TCD Credits = ₹1. Credits, wallet balances, rewards, and withdrawable cash are not necessarily interchangeable. Eligibility and applicable wallet rules determine what can be used or withdrawn.",
  },
  {
    q: "Are rewards or cash prizes guaranteed?",
    a: "No. Rewards and prizes depend on the specific exam or competition, its published rules, eligibility, and result verification. Check the terms shown for the relevant activity.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <Section className="relative overflow-hidden bg-[#050B1A] py-20 lg:py-28">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 top-[-10rem] h-[30rem] w-[30rem] rounded-full bg-brand/5 blur-[110px]" />
        <div className="absolute -right-40 bottom-[-12rem] h-[32rem] w-[32rem] rounded-full bg-brand-gold/10 blur-[120px]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/15 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="mx-auto max-w-3xl text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-brand/15 bg-white/80 px-4 py-2 shadow-sm backdrop-blur-xl">
            <HelpCircle className="h-4 w-4 text-brand-gold" />
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-brand">
              FAQ
            </span>
          </div>

          <h2 className="mt-7 text-4xl font-black leading-[1.06] tracking-tight text-white md:text-5xl lg:text-[3.5rem]">
            Frequently Asked{" "}
            <span className="text-brand-gold">Questions</span>
          </h2>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-white/70 md:text-lg md:leading-8">
            Clear answers about exams, monitoring, rankings, and TCD Credits.
          </p>
        </motion.div>

        <div className="mt-12 space-y-4 lg:mt-14">
          {faqs.map((faq, index) => {
            const isOpen = open === index;

            return (
              <motion.div
                key={faq.q}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.45, delay: index * 0.045 }}
                className={`group overflow-hidden rounded-[1.4rem] border bg-white shadow-[0_8px_30px_rgba(20,45,90,0.05)] transition-all duration-300 ${
                  isOpen
                    ? "border-brand-gold/40 shadow-[0_16px_45px_rgba(20,45,90,0.09)]"
                    : "border-brand/10 hover:border-brand/25"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center gap-4 px-5 py-5 text-left sm:gap-5 sm:px-7 sm:py-6"
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold transition-all duration-300 ${
                      isOpen
                        ? "bg-brand-gold text-brand"
                        : "bg-brand/[0.06] text-brand/60 group-hover:bg-brand-gold/10 group-hover:text-brand-gold"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span
                    className={`flex-1 text-sm font-bold leading-6 transition-colors duration-300 sm:text-base ${
                      isOpen ? "text-brand" : "text-brand/85 group-hover:text-brand"
                    }`}
                  >
                    {faq.q}
                  </span>

                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-all duration-300 ${
                      isOpen
                        ? "rotate-180 border-brand-gold/30 bg-brand-gold/10 text-brand-gold"
                        : "border-brand/10 bg-brand/[0.03] text-brand/60 group-hover:border-brand/20"
                    }`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                    >
                      <div className="px-5 pb-6 sm:px-7 sm:pb-7">
                        <div className="ml-0 border-l-2 border-brand-gold/30 pl-4 sm:ml-14 sm:pl-5">
                          <p className="max-w-3xl text-sm leading-7 text-brand-muted">
                            {faq.a}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-8 overflow-hidden rounded-[1.5rem] border border-white/10 bg-brand p-6 shadow-[0_18px_50px_rgba(20,45,90,0.13)] sm:p-7"
        >
          <div className="flex items-center gap-4 sm:gap-5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-brand-gold/25 bg-brand-gold/10 text-brand-gold">
              <MessageCircleQuestion className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <p className="text-sm font-bold text-white">Still have questions?</p>
              <p className="mt-1 text-xs leading-5 text-white/70 sm:text-sm">
                Contact us at{" "}
                <a
                  href="mailto:support@theconclusiondaily.com"
                  className="font-semibold text-brand-gold underline-offset-4 transition-colors hover:text-white hover:underline"
                >
                  support@theconclusiondaily.com
                </a>
              </p>
            </div>

            <ShieldCheck className="ml-auto hidden h-6 w-6 shrink-0 text-brand-gold/70 sm:block" />
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
